/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { type TaskFunction } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import { post } from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';

// eslint-disable-next-line @intercom/intercom/no-bare-strings
const ADMIN_UNPERMITTED_ERROR_MESSAGE = 'Admin does not have required permissions';
export default class FinOptInService extends Service {
  @service declare permissionsService: any;
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare intl: IntlService;
  @service declare notificationsService: any;

  @tracked showFinOptInModal = false;

  declare postAcceptCallback: TaskFunction<void, any>;

  get hasBillingPermissions() {
    let hasBillingPermissions = this.permissionsService.currentAdminCan(
      'can_access_billing_settings',
    );
    if (!hasBillingPermissions) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'blocked',
        object: 'fin_opt_in_modal',
        place: 'billing_permissions',
      });
    }
    return hasBillingPermissions;
  }

  get hasOptedInToFin() {
    return this.appService.app.hasOptedInToFin;
  }

  // If the workspace was created prior to the Fin General Release
  // Or is a cardless trial created on or after June 1st 2023 (Wave 3)
  // we will show opt in copy and automatically opt them in on set live of fin
  get bypassFinOptInModal() {
    return (
      this.appService.app.wasCreatedAfterFinGeneralRelease ||
      this.appService.app.wasCreatedAfterCardlessFinTrialRelease
    );
  }

  isUnpermittedError = (error: Error) => {
    return error?.message === ADMIN_UNPERMITTED_ERROR_MESSAGE;
  };

  @action
  openFinOptInModal() {
    if (this.bypassFinOptInModal) {
      taskFor(this.acceptTermsAndConditions).perform();
    } else {
      this.showFinOptInModal = true;
    }
  }

  @action
  closeFinOptInModal() {
    this.showFinOptInModal = false;
  }

  @task
  *postAcceptOptIn() {
    let analytics_event_object = this.bypassFinOptInModal
      ? 'background_opt_in'
      : 'fin_opt_in_modal';

    // First check if admin has permissions - if not, the permissions modal will be shown and
    // an error raised that will need to be handled upstream
    try {
      yield this.permissionsService.checkPermission(
        'can_access_billing_settings',
        ADMIN_UNPERMITTED_ERROR_MESSAGE,
      );
    } catch (error) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'blocked',
        object: analytics_event_object,
      });
      throw error;
    }

    let params = {
      app_id: this.appService.app.id,
    };
    try {
      yield post('/ember/fin_opt_in', params);

      this.intercomEventService.trackAnalyticsEvent({
        action: 'opted_in',
        object: analytics_event_object,
      });
      this.appService.app.set('hasOptedInToFin', true);
    } catch (error) {
      if (error?.jqXHR?.status !== 409) {
        captureException(new Error('Fin Opt In postAcceptOptIn failed'), {
          fingerprint: ['fin-opt-in-service', 'postAcceptOptIn'],
          extra: {
            bypassFinOptInModal: this.bypassFinOptInModal,
            hasOptedInToFin: this.hasOptedInToFin,
            appId: this.appService.app.id,
          },
          tags: {
            responsibleTeam: 'team-ai-agent',
            responsible_team: 'team-ai-agent',
          },
        });
        throw error;
      }
    }
  }

  @task
  *acceptTermsAndConditions() {
    try {
      yield taskFor(this.postAcceptOptIn).perform();
      this.closeFinOptInModal();
      if (!this.bypassFinOptInModal) {
        this.notificationsService.notifyConfirmation(this.intl.t('modal.fin-opt-in.success'));
      }
      yield taskFor(this.postAcceptCallback).perform();
    } catch (error) {
      this.notificationsService.notifyError(this.intl.t('modal.fin-opt-in.request_error'));
      captureException(new Error('Fin Opt In acceptTermsAndConditions failed'), {
        fingerprint: ['fin-opt-in-service', 'acceptTermsAndConditions'],
        extra: {
          bypassFinOptInModal: this.bypassFinOptInModal,
          has_opted_in_to_fin: this.hasOptedInToFin,
          app_id: this.appService.app.id,
        },
        tags: {
          responsibleTeam: 'team-ai-agent',
          responsible_team: 'team-ai-agent',
        },
      });
    }
  }

  @task
  *consentToExternalAi() {
    if (!this.permissionsService.currentAdminCan('can_access_workspace_settings')) {
      return this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_access_workspace_settings',
      );
    }
    try {
      this.appService.app.set('hasConsentedToExternalAi', true);
      yield post('/ember/external_ai_consent/opt_in', {
        app_id: this.appService.app.id,
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.ai-automation.external-ai.failed-to-toggle-external-ai-consent'),
      );
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    finOptInService: FinOptInService;
    'fin-opt-in-service': FinOptInService;
  }
}
